/**
 * PatientDemographicsPanel — Patient identity + historical diagnosis summary.
 *
 * Section 1 of the top layout on TranscriptionPage.
 * Contains: patient search, demographics (name, MRN, DOB, gender),
 * save button, and a compact historical diagnosis summary.
 */
import React, { useCallback } from 'react';
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockIcon from '@mui/icons-material/Lock';
import type { PatientContextInput, PatientResponse } from '../services/patientService';
import { GENDER_OPTIONS } from '../constants/radiology';
import { typography } from '~/theme';

interface PatientDemographicsPanelProps {
  value: PatientContextInput;
  onChange: (ctx: PatientContextInput) => void;
  onPatientSelected: (patient: PatientResponse | null) => void;
  disabled?: boolean;
  sessionId?: string;
}

const PatientDemographicsPanel: React.FC<PatientDemographicsPanelProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const handleField = useCallback(
    (field: keyof PatientContextInput) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange({ ...value, [field]: e.target.value || undefined });
      },
    [value, onChange],
  );

  const hasPatientInfo = !!(value.mrn || value.first_name || value.last_name);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <PersonOutlineIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight={typography.h1.fontWeight} sx={{ fontSize: typography.body2.fontSize }}>
          Patient Details
        </Typography>
        {hasPatientInfo && (
          <Chip
            icon={<LockIcon sx={{ fontSize: 12 }} aria-hidden="true" />}
            label="PHI"
            size="small"
            color="success"
            variant="outlined"
            sx={{ fontSize: typography.caption.fontSize, height: 20, fontWeight: typography.label.fontWeight, ml: 'auto' }}
          />
        )}
      </Box>

      {/* Demographics fields */}
      <Grid container spacing={1.5}>
        <Grid item xs={6}>
          <TextField
            label="First Name"
            value={value.first_name || ''}
            onChange={handleField('first_name')}
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Last Name"
            value={value.last_name || ''}
            onChange={handleField('last_name')}
            size="small"
            fullWidth
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="MRN"
            value={value.mrn || ''}
            onChange={handleField('mrn')}
            size="small"
            fullWidth
            disabled={disabled}
            placeholder="Medical Record #"
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="Date of Birth"
            type="date"
            value={value.dob || ''}
            onChange={handleField('dob')}
            size="small"
            fullWidth
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            select
            label="Gender"
            value={value.gender || ''}
            onChange={handleField('gender')}
            size="small"
            fullWidth
            disabled={disabled}
          >
            <MenuItem value="">—</MenuItem>
            {GENDER_OPTIONS.map((g) => (
              <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

    </Paper>
  );
};

export default PatientDemographicsPanel;
